/**
 * Consent (Cookiebot) helpers — US/CCPA opt-out posture.
 *
 * We are an opt-out jurisdiction (Territory = United States in Cookiebot):
 *   - Trackers fire by default for everyone.
 *   - They are suppressed ONLY when a user opts out OR arrives with GPC enabled.
 *   - There is NO interruptive banner; the opt-out surface is the
 *     "Your Privacy Choices" footer link, which calls openPrivacyChoices().
 *
 * Cookiebot consent categories we care about:
 *   - statistics -> PostHog (analytics)
 *   - marketing  -> Meta Pixel / advertising (governed via GTM/auto-block)
 *
 * Docs: https://www.cookiebot.com/en/developer/
 */

export interface CookiebotConsent {
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
  /**
   * How consent was obtained: "implied" (opt-out / fire-by-default — our US
   * posture) or "explicit" (opt-in / block-first). If this ever reports
   * "explicit" in production it would silently kill US tracking; we emit it on
   * the `consent_state` telemetry event so we can alert on the flip.
   */
  method?: 'implied' | 'explicit' | string;
}

interface CookiebotApi {
  consent: CookiebotConsent;
  /** Re-opens the consent dialog so the user can change/withdraw choices. */
  renew: () => void;
  /** Shows the consent dialog. */
  show?: () => void;
  /** True when the visitor's browser is sending a Global Privacy Control signal. */
  doNotTrack?: boolean;
}

declare global {
  interface Window {
    Cookiebot?: CookiebotApi;
  }
}

/**
 * Tracks whether the user has opened the "Your Privacy Choices" dialog during
 * this page load. ConsentBridge reads this to attribute a subsequent opt-out to
 * a deliberate footer click (`footer_click`) vs. a GPC browser-signal
 * auto-decline (`gpc_auto`). Module-level so it survives across the React
 * component lifecycle within a single page load. Not a tracked event itself.
 */
let privacyDialogOpened = false;

/** True if the user opened the privacy-choices dialog this page load. */
export function wasPrivacyDialogOpened(): boolean {
  return privacyDialogOpened;
}

/**
 * Opens the Cookiebot preference dialog ("Your Privacy Choices").
 * Safe to call before Cookiebot has loaded — it no-ops in that case.
 */
export function openPrivacyChoices(): void {
  const cb = window.Cookiebot;
  if (!cb) return;
  // Mark deliberate user intent to use the opt-out UI — used purely to label the
  // source of any resulting opt-out; the dialog open is NOT itself captured.
  privacyDialogOpened = true;
  if (typeof cb.renew === 'function') {
    cb.renew();
  } else if (typeof cb.show === 'function') {
    cb.show();
  }
}

/** Returns the current statistics-category consent (PostHog gate). Defaults to true (opt-out model). */
export function hasAnalyticsConsent(): boolean {
  // Opt-out model: absence of an explicit decline = consented.
  return window.Cookiebot?.consent?.statistics ?? true;
}
